// src/components/product/ProductSettingsSection.tsx

import { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ApiOutlined,
  CheckCircleOutline,
  RadioButtonUncheckedOutlined,
} from '@mui/icons-material';

import { ProductStatus, RequiredDataType } from '@models/product';
import { ProductSaleType, ProductType } from '@apis/product';
import {
  PRODUCT_SALE_TYPE_LABELS,
  PRODUCT_STATUS_LABELS,
  PRODUCT_TYPE_LABELS,
} from '@utils/productLabels';
import { getProductSaleTypeDisplayMeta } from './ProductSaleTypeDisplay';
import { getProductTypeDisplayMeta } from './PartnerProductSpecDisplays';

type StockOption = {
  value: number;
  label: string;
};

interface ProductSettingsSectionProps {
  productStatus: ProductStatus;
  productSaleType: ProductSaleType;
  productType: ProductType;
  requiredFields: RequiredDataType[];
  stockId: number | null;
  stockOptions: StockOption[];
  apiParameter: string | null;
  isApiInputNeeded: boolean;
  compact?: boolean;
  showProductStatus?: boolean;
  onChangeStatus: (value: ProductStatus) => void;
  onChangeSaleType: (value: ProductSaleType) => void;
  onChangeProductType: (value: ProductType) => void;
  onToggleRequiredField: (field: RequiredDataType, checked: boolean) => void;
  onChangeStockId: (value: number | null) => void;
  onChangeApiParameter: (value: string | null) => void;
}

/**
 * Returns the allowed ProductType list for a given Order Type (sale type).
 *
 * REAL_TIME    → ESIM, PSIM_TOPUP
 * RESERVATION  → RESERVE_ESIM, ROUTER
 */
const getAllowedProductTypes = (saleType: ProductSaleType): ProductType[] => {
  if (saleType === ProductSaleType.REAL_TIME) {
    return [ProductType.ESIM, ProductType.PSIM_TOPUP];
  }
  if (saleType === ProductSaleType.RESERVATION) {
    return [ProductType.RESERVE_ESIM, ProductType.ROUTER];
  }
  return Object.values(ProductType);
};

const segmentedButtonGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 0.75,
} as const;

const segmentedButtonSx = {
  minWidth: 0,
  justifyContent: 'flex-start',
  px: 1,
  whiteSpace: 'normal',
  lineHeight: 1.2,
  textTransform: 'none',
  fontWeight: 800,
  borderColor: 'divider',
  '& .MuiButton-startIcon': {
    mr: 0.6,
  },
} as const;

const softDividerSx = {
  my: 0.5,
  borderColor: 'rgba(0, 0, 0, 0.06)',
} as const;

const requiredFieldLabels: Record<RequiredDataType, string> = {
  [RequiredDataType.ACTIVATION_DATE]: 'Activation Date',
  [RequiredDataType.VALIDITY_DAYS]: 'Validity Days',
  [RequiredDataType.IMEI]: 'IMEI',
  [RequiredDataType.EID]: 'EID',
  [RequiredDataType.CUSTOMER_NAME]: 'Name',
  [RequiredDataType.PASSPORT_DATA]: 'Passport',
};

const visibleRequiredFields = [
  RequiredDataType.ACTIVATION_DATE,
  RequiredDataType.IMEI,
  RequiredDataType.EID,
  RequiredDataType.CUSTOMER_NAME,
  RequiredDataType.PASSPORT_DATA,
] as const;

/**
 * ProductSettingsSection
 *
 * High-level configuration panel for a product:
 * 1) Order Type / Product Type / Status
 * 2) Required customer fields
 * 3) Stock linkage and optional API parameter (JSON)
 *
 * Important UX / state guard:
 * - When Order Type changes, the allowed Product Type options change immediately.
 * - MUI Select warns if its current `value` does not exist in the new option list.
 * - To avoid transient "out-of-range value" warnings, we bind the Select to a
 *   "safe" value that is always present in the current option list, while still
 *   normalizing the upstream state via an effect.
 */
const ProductSettingsSection = ({
  productStatus,
  productSaleType,
  productType,
  requiredFields,
  stockId,
  stockOptions,
  apiParameter,
  isApiInputNeeded,
  compact = false,
  showProductStatus = true,
  onChangeStatus,
  onChangeSaleType,
  onChangeProductType,
  onToggleRequiredField,
  onChangeStockId,
  onChangeApiParameter,
}: ProductSettingsSectionProps) => {
  const allowedProductTypes = useMemo(
    () => getAllowedProductTypes(productSaleType),
    [productSaleType],
  );

  /**
   * "Safe" value for the Product Type Select.
   * This prevents MUI out-of-range warnings during the render where the option list changes.
   */
  const safeProductType = useMemo<ProductType>(() => {
    return allowedProductTypes.includes(productType)
      ? productType
      : allowedProductTypes[0];
  }, [allowedProductTypes, productType]);

  /**
   * Normalize the actual productType state after Order Type changes.
   * This keeps the persisted state consistent with the allowed combination rules.
   */
  useEffect(() => {
    if (productType !== safeProductType) {
      onChangeProductType(safeProductType);
    }
  }, [productType, safeProductType, onChangeProductType]);

  const isRequired = (field: RequiredDataType) => requiredFields.includes(field);

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      })}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Product settings
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Configure product type, required customer fields, and stock / API linkage.
          </Typography>
        }
      />

      <CardContent>
        <Stack spacing={3}>
          {/* 1) Product type & status */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              {showProductStatus ? '1. Product type & status' : '1. Product type'}
            </Typography>

            <Stack
              spacing={2}
            >
              {/* Order Type (Sale Type) */}
              <Stack spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 800, textTransform: 'uppercase' }}
                >
                  Order Type
                </Typography>

                <Box role="group" aria-label="Order Type" sx={segmentedButtonGridSx}>
                  {Object.values(ProductSaleType).map((t) => (
                    <Button
                      key={t}
                      variant={productSaleType === t ? 'contained' : 'outlined'}
                      color={productSaleType === t ? 'primary' : 'inherit'}
                      startIcon={getProductSaleTypeDisplayMeta(t).icon}
                      onClick={() => onChangeSaleType(t)}
                      sx={segmentedButtonSx}
                    >
                      {PRODUCT_SALE_TYPE_LABELS[t]}
                    </Button>
                  ))}
                </Box>
              </Stack>

              {/* Product Type (options depend on Order Type) */}
              <Stack spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 800, textTransform: 'uppercase' }}
                >
                  Product Type
                </Typography>

                <Box role="group" aria-label="Product Type" sx={segmentedButtonGridSx}>
                  {allowedProductTypes.map((type) => (
                    <Button
                      key={type}
                      variant={safeProductType === type ? 'contained' : 'outlined'}
                      color={safeProductType === type ? 'primary' : 'inherit'}
                      startIcon={getProductTypeDisplayMeta(type).icon}
                      onClick={() => onChangeProductType(type)}
                      sx={segmentedButtonSx}
                    >
                      {PRODUCT_TYPE_LABELS[type]}
                    </Button>
                  ))}
                </Box>
              </Stack>

              {showProductStatus ? (
                <FormControl size="small" sx={{ minWidth: compact ? 0 : 200, width: compact ? '100%' : 'auto' }}>
                  <InputLabel id="product-status-label">Product Status</InputLabel>
                  <Select
                    labelId="product-status-label"
                    label="Product Status"
                    value={productStatus}
                    onChange={(e) => onChangeStatus(e.target.value as ProductStatus)}
                  >
                    {Object.values(ProductStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {PRODUCT_STATUS_LABELS[status]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Stack>
          </Box>

          <Divider sx={softDividerSx} />

          {/* 2) Required customer fields */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              2. Required customer fields
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              {visibleRequiredFields.map((field) => {
                const checked = isRequired(field);

                return (
                  <Button
                    key={field}
                    variant="outlined"
                    color={checked ? 'primary' : 'inherit'}
                    startIcon={
                      checked ? (
                        <CheckCircleOutline sx={{ fontSize: 17 }} />
                      ) : (
                        <RadioButtonUncheckedOutlined sx={{ fontSize: 17 }} />
                      )
                    }
                    onClick={() => onToggleRequiredField(field, !checked)}
                    sx={(theme) => ({
                      minHeight: 40,
                      justifyContent: 'flex-start',
                      px: 1,
                      textTransform: 'none',
                      fontWeight: 800,
                      lineHeight: 1.2,
                      color: checked ? theme.palette.primary.main : theme.palette.text.secondary,
                      borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                      bgcolor: checked ? 'rgba(25, 118, 210, 0.06)' : 'background.paper',
                      '&:hover': {
                        borderColor: checked ? theme.palette.primary.main : theme.palette.text.secondary,
                        bgcolor: checked ? 'rgba(25, 118, 210, 0.09)' : 'action.hover',
                      },
                      '& .MuiButton-startIcon': {
                        mr: 0.7,
                      },
                    })}
                  >
                    {requiredFieldLabels[field]}
                  </Button>
                );
              })}
            </Box>
          </Box>

          <Divider sx={softDividerSx} />

          {/* 3) Stock settings */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              3. Stock settings
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Select the stock code used when this product is ordered.
            </Typography>

            <FormControl size="small" sx={{ minWidth: compact ? 0 : 260, width: compact ? '100%' : 'auto' }}>
              <Select
                value={stockId ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  onChangeStockId(value ? Number(value) : null);
                }}
                inputProps={{ 'aria-label': 'Stock Code' }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {stockOptions.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isApiInputNeeded ? (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.info.light}`,
                  bgcolor: 'rgba(2, 136, 209, 0.04)',
                  p: 1.5,
                })}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ApiOutlined sx={{ fontSize: 17, color: 'info.main' }} />
                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'info.main' }}>
                      API configuration
                    </Typography>
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Supplier API parameters sent with this stock request. Keep the value as valid JSON.
                  </Typography>

                  <TextField
                    placeholder='{"key": "value"}'
                    value={apiParameter ?? ''}
                    onChange={(e) => onChangeApiParameter(e.target.value || null)}
                    size="small"
                    fullWidth
                    InputProps={{
                      inputComponent: 'textarea' as any,
                      inputProps: {
                        'aria-label': 'API parameter JSON',
                        spellCheck: false,
                        style: {
                          resize: 'vertical',
                          minHeight: 120,
                          overflow: 'auto',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(15, 23, 42, 0.03)',
                        alignItems: 'flex-start',
                      },
                      '& .MuiInputBase-input': {
                        fontFamily: 'ui-monospace, SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace',
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: 'text.primary',
                        tabSize: 2,
                      },
                    }}
                  />
                </Stack>
              </Box>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductSettingsSection;
